import React, { useEffect, useState } from 'react';
import { AlertTriangle, ChevronRight } from 'lucide-react';
import { WorkflowRow } from '../../services/emailWorkflowApi';
import { formatINR } from '../../utils/currencyFormatter';
import { ApprovalBadge, FinanceBadge, TierBadge, facilityLabel } from './badges';
import { WorkflowDetailBody } from './WorkflowDetailBody';

/**
 * The shared request table + accordion detail row — used by the requester board
 * and the Payment Request Approvals page. `financeActions` is passed straight
 * through to WorkflowDetailBody (only the approvals page turns it on).
 */

// API returns amounts as strings ("6000.00") — coerce before formatting.
const num = (v: unknown): number | null => {
	if (v == null) return null;
	const n = Number(v);
	return Number.isFinite(n) ? n : null;
};
const fmtAmount = (r: WorkflowRow) => {
	const a = num(r.attachment_amount) ?? num(r.email_claimed_amount);
	return a == null ? '—' : formatINR(a);
};
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const fmtDate = (iso: string | null | undefined) => {
	if (!iso) return '—';
	// Show the UTC calendar date so the displayed day MATCHES the backend date
	// filter (which ranges on the UTC date part). Rendering via new Date(...)
	// .toLocaleDateString shifts by the viewer's tz — e.g. "2026-05-12T19:30Z"
	// would show as 13 May in IST while the filter treats it as 12 May. Parse the
	// date part directly instead so filter and display always agree.
	const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
	if (m) {
		const [, y, mo, day] = m;
		return `${day} ${MONTHS[Number(mo) - 1]} ${y.slice(2)}`;
	}
	const d = new Date(iso);
	return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' });
};

/** true below Tailwind's sm breakpoint. We SWITCH between card list and table in
 *  JS (not CSS hide) so the expanded WorkflowDetailBody mounts exactly once —
 *  CSS-hiding both trees double-fetched every detail and broke strict-mode specs. */
export const useIsMobile = () => {
	const [mobile, setMobile] = useState(() => window.matchMedia('(max-width: 639px)').matches);
	useEffect(() => {
		const mq = window.matchMedia('(max-width: 639px)');
		const fn = (e: MediaQueryListEvent) => setMobile(e.matches);
		mq.addEventListener('change', fn);
		return () => mq.removeEventListener('change', fn);
	}, []);
	return mobile;
};

export const WorkflowTable: React.FC<{
	rows: WorkflowRow[];
	openThreadId: string | null;
	onToggle: (threadId: string | null) => void;
	onRowChanged: (message?: string) => void;
	financeActions?: boolean;
	/** Row numbering offset — pass (page-1)*page_size so numbers continue across pages. */
	startIndex?: number;
}> = ({ rows, openThreadId, onToggle, onRowChanged, financeActions = false, startIndex = 0 }) => {
	// backend ships request_date (approval-requested = first email date) later; adapt when present.
	const hasRequestDate = rows.some(r => r.request_date != null);
	const isMobile = useIsMobile();

	// mobile (<sm): card list — Option 1 of the mobile redesign (2026-07-05)
	if (isMobile) return (
		<div className="space-y-3">
			{rows.map((r, i) => {
				const flagged = r.amount_match === 0 || r.counterparty_uncertain === 1;
				const expanded = openThreadId === r.thread_id;
				return (
					<div
						key={r.thread_id}
						className={`rounded-lg border bg-white ${expanded ? 'border-primary/40' : 'border-gray-200'}`}
					>
						<button
							type="button"
							data-testid="request-card"
							onClick={() => onToggle(expanded ? null : r.thread_id)}
							aria-expanded={expanded}
							className="w-full text-left p-3"
						>
							<div className="flex items-start justify-between gap-2">
								<div className="min-w-0">
									<div className="flex items-center gap-1.5">
										<span className="text-[11px] text-gray-400 tabular-nums">#{startIndex + i + 1}</span>
										{flagged && (
											<AlertTriangle className="h-3.5 w-3.5 text-amber-500 shrink-0" aria-label="Needs a look" />
										)}
										<span className="font-semibold text-sm text-gray-900 truncate">
											{r.counterparty || <span className="text-gray-400 font-normal">Unknown</span>}
										</span>
									</div>
									<p className="text-xs text-gray-400 truncate mt-0.5">{r.gist}</p>
								</div>
								<span className="font-semibold text-sm text-gray-900 tabular-nums shrink-0">{fmtAmount(r)}</span>
							</div>
							<div className="flex flex-wrap items-center gap-1.5 mt-2">
								<ApprovalBadge status={r.effective_approval_status} source={r.effective_approval_source} />
								<FinanceBadge status={r.finance_status} />
							</div>
							<div className="flex items-center justify-between mt-2 text-xs text-gray-400">
								<span className="truncate">
									{facilityLabel(r.facility_key) || r.city || '—'} · {fmtDate(r.request_date ?? r.created_at)}
								</span>
								<ChevronRight
									className={`h-4 w-4 shrink-0 transition-transform ${expanded ? 'rotate-90' : ''}`}
								/>
							</div>
						</button>
						{expanded && (
							<div className="border-t border-gray-100 bg-gray-50/70 p-3">
								<WorkflowDetailBody threadId={r.thread_id} onChanged={onRowChanged} financeActions={financeActions} />
							</div>
						)}
					</div>
				);
			})}
		</div>
	);

	// desktop (sm+): the table
	return (
		<div className="overflow-x-auto">
			<table className="w-full text-[13px]">
				<thead>
					<tr className="border-b border-gray-200 text-left text-gray-500">
						<th className="py-2 pr-2 font-medium text-gray-400">#</th>
						<th className="py-2 pr-3 font-medium">Counterparty</th>
						<th className="py-2 pr-3 font-medium">City</th>
						<th className="py-2 pr-3 font-medium">Facility</th>
						<th className="py-2 pr-3 font-medium text-right">Amount</th>
						<th className="py-2 pr-3 font-medium">Approval</th>
						<th className="py-2 pr-3 font-medium">Finance</th>
						<th className="py-2 pr-3 font-medium">Where</th>
						<th
							className="py-2 font-medium text-right"
							title={
								hasRequestDate
									? 'When approval was requested (date of the first email in the thread)'
									: 'When the request was added to the dashboard (not the email date)'
							}
						>
							{hasRequestDate ? 'Requested' : 'Added'}
						</th>
					</tr>
				</thead>
				<tbody>
					{rows.map((r, i) => {
						const flagged = r.amount_match === 0 || r.counterparty_uncertain === 1;
						const expanded = openThreadId === r.thread_id;
						return (
							<React.Fragment key={r.thread_id}>
							<tr
								onClick={() => onToggle(expanded ? null : r.thread_id)}
								aria-expanded={expanded}
								className={`border-b border-gray-100 last:border-0 hover:bg-gray-50 cursor-pointer ${
								expanded ? 'bg-primary/5' : ''
							}`}
							>
								<td className="py-2 pr-2 text-gray-400 tabular-nums">{startIndex + i + 1}</td>
								<td className="py-2 pr-3 max-w-[240px]">
									<div className="flex items-center gap-1.5">
										<ChevronRight
											className={`h-3.5 w-3.5 shrink-0 text-gray-400 transition-transform ${expanded ? 'rotate-90' : ''}`}
										/>
										{flagged && (
											<AlertTriangle
												className="h-3.5 w-3.5 text-amber-500 shrink-0"
												aria-label="Needs a look"
											/>
										)}
										<span className="text-gray-900 truncate">
											{r.counterparty || <span className="text-gray-400">Unknown</span>}
										</span>
									</div>
									<div className="text-gray-400 truncate text-xs pl-5">{r.gist}</div>
								</td>
								<td className="py-2 pr-3">
									{r.city || <span className="text-gray-400">Unattributed</span>}
								</td>
								<td className="py-2 pr-3 text-gray-600">{facilityLabel(r.facility_key) || '—'}</td>
								<td className="py-2 pr-3 text-right tabular-nums text-gray-900">{fmtAmount(r)}</td>
								<td className="py-2 pr-3">
									<ApprovalBadge status={r.effective_approval_status} source={r.effective_approval_source} />
								</td>
								<td className="py-2 pr-3">
									<FinanceBadge status={r.finance_status} />
								</td>
								<td className="py-2 pr-3">
									<TierBadge
										role={r.current_tier_role}
										effectiveApproved={r.effective_approval_status === 'approved'}
										financeStatus={r.finance_status}
									/>
								</td>
								<td className="py-2 text-right text-gray-500 tabular-nums">
									{fmtDate(r.request_date ?? r.created_at)}
								</td>
							</tr>
							{/* accordion detail — row overview above, full detail inline below */}
							{expanded && (
								<tr className="border-b border-gray-100 last:border-0">
									<td colSpan={9} className="bg-gray-50/70 px-4 py-4">
										<WorkflowDetailBody
											threadId={r.thread_id}
											onChanged={onRowChanged}
											financeActions={financeActions}
										/>
									</td>
								</tr>
							)}
							</React.Fragment>
						);
					})}
				</tbody>
			</table>
		</div>
	);
};
